<template>
  <div class="series-compare-stock-chart-wrapper">
    <div ref="chartContainer"></div>
    <div class="d-flex justify-content-center" v-if="enableLegend">
      <div class="custom-legend-container">
        <div
          v-for="(chartData, index) in chartSeries"
          :key="index"
          class="custom-legend"
          draggable="true"
          @click="onClickCustomLegends(chartData)"
          @dragstart="onDragStartSortChartData(chartData.zIndex)"
          @dragover.prevent
          @drop.prevent="onDropSortChartData(chartData.zIndex)"
        >
          <span
            :style="getCustomLegendStyle(chartData)"
            class="horizontal-stroke"
          >
            &#x2501;&#x2501;
          </span>
          <span :class="{ 'disabled-legend': !chartData.visible }">{{
            chartData.name
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// module import

import Highcharts from 'highcharts';
import Boost from 'highcharts/modules/boost';
import Stock from 'highcharts/modules/stock';
import ExportData from 'highcharts/modules/export-data';
import Exporting from 'highcharts/modules/exporting';
import Accessibility from 'highcharts/modules/accessibility';

// inialize highchart modules
Boost(Highcharts);
Stock(Highcharts);
ExportData(Highcharts);
Exporting(Highcharts);
Accessibility(Highcharts);

// helper functions
import {
  timestampToCETConvertor,
  convertDateToCETDate,
} from '../../utils/helpers.js';

export default {
  name: 'SeriesCompareStockChart',
  data() {
    return {
      chart: undefined,
      chartSeries: null,
      cloneOfChartSeries: null,
      currentDragIndex: null,
      currentMouseOverPointDetails: null,
      isDataGrouping: false,
    };
  },
  props: {
    seriesDetails: {
      type: Array,
      required: true,
      default: () => [],
    },
    redrawChart: {
      type: Boolean,
      default: false,
    },
    enableXAxiesLabels: {
      type: Boolean,
      required: false,
      default: true,
    },
    enableYAxiesLabels: {
      type: Boolean,
      required: false,
      default: true,
    },
    enableTooltip: {
      required: false,
      type: Boolean,
      default: true,
    },
    enableAnimation: {
      type: Boolean,
      default: true,
      required: false,
    },
    lineWidth: {
      type: Number,
      required: false,
      default: 1,
    },
    lineColor: {
      type: String,
      required: false,
      default: '#adb9d2',
    },
    enableLegend: {
      type: Boolean,
      default: true,
    },
    timeSeriesData: {
      required: false,
      type: Array,
      default: () => [],
    },
  },

  methods: {
    createChart() {
      const vm = this;
      this.chart = Highcharts.stockChart(this.$refs.chartContainer, {
        timezone: 'Europe/Berlin',
        chart: {
          type: 'line',
          height: 450,
          zoomType: 'x',
          animation: this.enableAnimation,
        },
        rangeSelector: {
          selected: 6,
          buttons: [
            {
              type: 'day',
              count: 3,
              text: '3t',
            },
            {
              type: 'day',
              count: 5,
              text: '5t',
            },
            {
              type: 'week',
              count: 1,
              text: '1w',
            },
            {
              type: 'month',
              count: 1,
              text: '1m',
            },
            {
              type: 'month',
              count: 6,
              text: '6m',
            },
            {
              type: 'year',
              count: 1,
              text: '1j',
            },
            {
              type: 'all',
              text: 'Alles',
            },
          ],
          inputStyle: {
            color: '#039',
            outline: 'none',
            padding: '5px',
            'justify-content': 'center',
            'align-items': 'center',
            display: 'flex',
          },
        },
        xAxis: {
          labels: {
            enabled: this.enableXAxiesLabels,
          },
          crosshair: {
            color: 'grey',
          },
          type: 'datetime',
          ordinal: false,
        },
        yAxis: {
          labels: {
            enabled: this.enableYAxiesLabels,
          },
          title: null,
          opposite: false,
          offset: 5,
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          enabled: this.enableTooltip,
          useHTML: true,
          split: false,
          shared: false,
          borderWidth: 2,
          formatter() {
            const tooltipDetails = vm.getTooltipDetails(this.point.x);
            return (
              '<b>' +
              'Zeitstempel: ' +
              timestampToCETConvertor(this.point.x, 'DD.MM.YY HH:mm') +
              '<br>Kalenderwoche: ' +
              tooltipDetails.totalWeekNumber.toLocaleString('de-DE') +
              '<br>Tag: ' +
              tooltipDetails.chartHoverDay +
              '<br>Wert: ' +
              this.point.y.toLocaleString('de-DE') +
              '</b>'
            );
          },
          positioner: function (labelWidth, labelHeight, point) {
            let chart = this.chart;
            let tooltipX, tooltipY;
            tooltipX = vm.currentMouseOverPointDetails.plotX;
            tooltipY = vm.currentMouseOverPointDetails.plotY;
            if (tooltipX + labelWidth > chart.chartWidth) {
              tooltipX -= labelWidth;
            }
            return {
              x: tooltipX,
              y: tooltipY,
            };
          },
        },
        legend: {
          enabled: false, // created custom legend
        },
        navigator: {
          adaptToUpdatedData: true, // Disable automatic adaptation of navigator to updated data
          xAxis: {
            ordinal: false,
          },
        },
        scrollbar: {
          liveRedraw: true,
        },
        plotOptions: {
          series: {
            dataGrouping: {
              enabled: this.isDataGrouping,
            },
            marker: {
              enabled: false,
              radius: 0,
            },
            turboThreshold: 1000000,
            showInNavigator: true,
            stickyTracking: false,
            allowPointSelect: false,
            lineWidth: this.lineWidth,
            states: {
              hover: {
                lineWidth: this.lineWidth,
              },
            },
            boostThreshold: 1,
            point: {
              events: {
                mouseOver: function () {
                  vm.currentMouseOverPointDetails = this;
                },
                mouseOut: function () {
                  vm.currentMouseOverPointDetails = null;
                },
              },
            },
          },
        },
        navigation: {
          buttonOptions: {
            enabled: false,
          },
        },
        series: [...this.chartSeries],
      });
    },
    redraw() {
      this.chart.series[0].setData(
        JSON.parse(JSON.stringify(this.seriesDetails[0].data)),
        true
      );
      this.cloneOfChartSeries = JSON.parse(
        JSON.stringify(this.seriesDetails[0].data)
      );
    },
    getTooltipDetails(date) {
      const weekDays = [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ];
      const chartHoverDate = convertDateToCETDate(date);
      const chartHoverDay = weekDays[chartHoverDate.day()];
      const totalWeekNumber = chartHoverDate.isoWeek();
      return {
        chartHoverDay,
        totalWeekNumber,
      };
    },

    getCurrentChartSeries(chartSeriesData) {
      return this.chart.series.find(
        (series) =>
          series.options && series.options.seriesId === chartSeriesData.seriesId
      );
    },

    getCustomLegendStyle(chartSeriesData) {
      const currentSeries = this.getCurrentChartSeries(chartSeriesData);
      return chartSeriesData.visible
        ? { color: currentSeries.color }
        : { color: '#cccccc' };
    },

    onDragStartSortChartData(chartZIndex) {
      this.currentDragIndex = chartZIndex;
    },

    onDropSortChartData(chartZindex) {
      const currentDraggedItem = this.chartSeries[this.currentDragIndex];
      const droppedItemIndex = this.chartSeries.findIndex(
        (i) => i.zIndex === this.currentDragIndex
      );
      const currentIndex = this.chartSeries.findIndex(
        (i) => i.zIndex === chartZindex
      );

      if (currentIndex !== droppedItemIndex) {
        this.chartSeries.splice(droppedItemIndex, 1);
        this.chartSeries.splice(currentIndex, 0, currentDraggedItem);
      }

      this.chartSeries.forEach((item, index) => {
        const currentSeries = this.getCurrentChartSeries(item);
        item.color = currentSeries.color;
        item.zIndex = index;
      });

      this.chart.update({
        series: this.chartSeries,
      });

      this.currentDragIndex = null;
    },

    onClickCustomLegends(chartSeriesData) {
      const currentChartSeries = this.getCurrentChartSeries(chartSeriesData);
      if (!chartSeriesData.visible) {
        chartSeriesData.visible = true;
        currentChartSeries.show();
      } else {
        chartSeriesData.visible = false;
        currentChartSeries.hide();
      }
    },

    verifyTimestampDifferenceBetweenSeries() {
      let initialDifference = null;
      let allSameDifference = true;

      this.chartSeries.forEach((series) => {
        if (series.data.length < 2) {
          allSameDifference = false;
          return;
        }
        const timestamp1 = series.data[0][0];
        const timestamp2 = series.data[1][0];
        const difference = timestamp2 - timestamp1;
        if (initialDifference === null) {
          initialDifference = difference;
        } else if (difference !== initialDifference) {
          allSameDifference = false;
        }
      });
      if (!allSameDifference) {
        this.isDataGrouping = true;
      }
    },
  },

  mounted() {
    Highcharts.setOptions({
      lang: {
        months: [
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ],
        weekdays: [
          'Sonntag',
          'Montag',
          'Dienstag',
          'Mittwoch',
          'Donnerstag',
          'Freitag',
          'Samstag',
        ],
        shortMonths: [
          'Jan',
          'Feb',
          'Mär',
          'Apr',
          'Mai',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Okt',
          'Nov',
          'Dez',
        ],
        decimalPoint: ',',
        thousandsSep: '.',
        rangeSelectorFrom: 'Von',
        rangeSelectorTo: 'Bis',
        rangeSelectorZoom: 'Zeitraum',
        resetZoom: 'Zoom zurücksetzen',
        resetZoomTitle: 'Zoom zurücksetzen',
        downloadPNG: 'Download als PNG',
        downloadJPEG: 'Download als JPEG',
        downloadPDF: 'Download als PDF',
        downloadSVG: 'Download als SVG',
        printChart: 'Drucken',
        noData: 'Keine Daten vorhanden',
      },
      time: {
        timezone: 'Europe/Berlin',
      },
    });

    //Make sure you clone the original chart data because highchart will update your data which you pass in chart series
    if (!Array.isArray(this.seriesDetails)) {
      this.cloneOfChartSeries = [...this.seriesDetails.data];
      this.chartSeries = [
        { ...this.seriesDetails, data: [...this.cloneOfChartSeries] },
      ];
    } else {
      if (this.timeSeriesData && this.timeSeriesData.length) {
        const seriesWithMeanValuesMap = new Map();
        this.timeSeriesData.forEach((seriesData) => {
          const totalOfBasePeakDifference =
            seriesData.basePeakPercentage?.reduce((acc, curr) => {
              acc += curr?.base * 100 - curr?.peak * 100;

              return acc;
            }, 0);
          seriesWithMeanValuesMap.set(
            seriesData.name,
            totalOfBasePeakDifference / seriesData.basePeakPercentage?.length
          );
        });
        this.chartSeries = [...this.timeSeriesData]
          .sort(
            (prevValue, nextValue) =>
              seriesWithMeanValuesMap.get(prevValue.name) -
              seriesWithMeanValuesMap.get(nextValue.name)
          )
          .map((seriesData, index) => ({
            seriesId: seriesData.id,
            name: seriesData.name,
            data: seriesData.data,
            zIndex: index,
            visible: true,
          }));
      } else if (this.seriesDetails.length > 1) {
        this.chartSeries = this.seriesDetails;
      } else {
        this.cloneOfChartSeries = [...this.seriesDetails[0].data];
        this.chartSeries = [
          { ...this.seriesDetails[0], data: [...this.cloneOfChartSeries] },
        ];
      }
    }
    this.verifyTimestampDifferenceBetweenSeries();
    this.createChart();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  watch: {
    redrawChart() {
      // Redraw chart
      if (this.redrawChart) this.redraw();
    },
  },
};
</script>

<style scoped>
.series-compare-stock-chart-wrapper .custom-legend-container {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
}

.series-compare-stock-chart-wrapper .custom-legend {
  cursor: pointer;
  color: rgb(66, 64, 64);
  font-size: 12px;
  font-weight: 600;
}
.series-compare-stock-chart-wrapper .custom-legend:hover {
  font-weight: bold;
  color: #15181f;
}
.series-compare-stock-chart-wrapper .disabled-legend {
  color: #cccccc;
}
.series-compare-stock-chart-wrapper .custom-legend .horizontal-stroke {
  display: inline-flex;
  margin-right: 5px;
  position: relative;
  top: -1px;
}

@media (max-width: 1199px) {
  .series-compare-stock-chart-wrapper .custom-legend {
    width: calc(33.33% - 10px);
  }
}
@media (max-width: 991px) {
  .series-compare-stock-chart-wrapper .custom-legend {
    width: calc(50% - 10px);
    text-align: left;
  }
}
@media (max-width: 767px) {
  .series-compare-stock-chart-wrapper .custom-legend {
    width: 100%;
  }
}
</style>
